import { useState } from 'react'
import { ChevronDown, ChevronUp, Search } from 'lucide-react'
import Section from '../components/Section'
import Button from '../components/Button'

const faqCategories = [
    {
        category: 'Getting Started',
        questions: [
            {
                question: 'What happens after my 14-day trial?',
                answer: "Your trial includes full access to all Pro features. After 14 days, you'll be downgraded to a limited free tier (text-only capture, no auto-scheduling). Upgrade anytime to restore full functionality.",
            },
            {
                question: 'Do I need a credit card to start the trial?',
                answer: "No! You can start your 14-day free trial without any payment information. We'll only ask for payment if you decide to upgrade after experiencing the full product.",
            },
            {
                question: 'How do I get started with voice capture?',
                answer: "After signing up, download our mobile app or install the desktop widget. You can start capturing immediately by pressing the microphone button and speaking naturally. Way2Will will automatically extract tasks, events, and notes.",
            },
        ],
    },
    {
        category: 'Features & Capabilities',
        questions: [
            {
                question: 'How is Way2Will different from Motion or Sunsama?',
                answer: "Motion uses rigid algorithms; Sunsama requires manual drag-and-drop. Way2Will combines voice-first capture with flexible AI that negotiates with you, not at you. Plus, our Chief of Staff memory gives you long-term contextual intelligence.",
            },
            {
                question: 'What integrations do you support?',
                answer: "We support Google Calendar, Outlook, Gmail, Slack, Notion, Linear, Jira, Trello, Asana, and more. All integrations feature two-way sync so changes reflect everywhere.",
            },
            {
                question: 'Can Way2Will work offline?',
                answer: "Voice capture works offline—your recordings are queued and processed when you reconnect. The AI features require an internet connection for real-time processing.",
            },
            {
                question: 'How accurate is the voice transcription?',
                answer: "Way2Will achieves 95%+ accuracy in most conditions, with real-time transcription under 800ms latency. The AI also learns your vocabulary and common phrases over time.",
            },
        ],
    },
    {
        category: 'Privacy & Security',
        questions: [
            {
                question: 'Is my voice data private and secure?',
                answer: "Absolutely. We're GDPR and BIPA compliant. Your voice data is encrypted in transit and at rest, never sold to third parties, and never used to train external AI models. You can delete all your data with one click.",
            },
            {
                question: 'Where is my data stored?',
                answer: "Your data is stored in secure, SOC 2 compliant data centers in the United States and EU (depending on your location). Enterprise customers can request specific data residency requirements.",
            },
            {
                question: 'Can I export my data?',
                answer: "Yes! You can export all your tasks, notes, journal entries, and calendar data at any time in standard formats (JSON, CSV, ICS).",
            },
        ],
    },
    {
        category: 'Billing & Plans',
        questions: [
            {
                question: 'Can I cancel anytime?',
                answer: "Yes, absolutely. Cancel with one click from your account settings. No questions asked. You'll retain access until the end of your current billing period.",
            },
            {
                question: 'Do you offer student or nonprofit discounts?',
                answer: "Yes! Students get 50% off Pro. Nonprofits get 30% off. Contact our support team with verification (student ID or nonprofit registration).",
            },
            {
                question: 'What payment methods do you accept?',
                answer: "All major credit cards (Visa, Mastercard, Amex, Discover) via Stripe. Enterprise customers can pay via invoice with NET-30 terms.",
            },
            {
                question: 'Can I upgrade from Pro to Team later?',
                answer: "Absolutely. Upgrade anytime from your account settings. Your data, history, and preferences transfer seamlessly. You'll be prorated for the remainder of your billing period.",
            },
        ],
    },
]

export default function FAQ() {
    const [searchQuery, setSearchQuery] = useState('')
    const [openItems, setOpenItems] = useState<Record<string, boolean>>({})

    const toggleItem = (id: string) => {
        setOpenItems(prev => ({ ...prev, [id]: !prev[id] }))
    }

    const filteredCategories = faqCategories.map(category => ({
        ...category,
        questions: category.questions.filter(
            q =>
                q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                q.answer.toLowerCase().includes(searchQuery.toLowerCase())
        ),
    })).filter(category => category.questions.length > 0)

    return (
        <>
            {/* Hero Section */}
            <Section className="pt-12 lg:pt-20">
                <div className="text-center max-w-4xl mx-auto">
                    <h1 className="text-4xl lg:text-5xl font-bold text-primary-color leading-tight mb-6">
                        Frequently Asked Questions
                    </h1>
                    <p className="text-lg lg:text-xl font-normal text-secondary-color leading-relaxed mb-8">
                        Everything you need to know about Way2Will. Can't find what you're looking for? Contact our support team.
                    </p>

                    {/* Search */}
                    <div className="max-w-xl mx-auto relative">
                        <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-tertiary-color" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search questions..."
                            className="w-full pl-12 pr-4 py-4 glass rounded-full focus:outline-none focus:border-primary-base/25 text-base"
                        />
                    </div>
                </div>
            </Section>

            {/* FAQ Categories */}
            <Section>
                <div className="max-w-3xl mx-auto">
                    {filteredCategories.map((category) => (
                        <div key={category.category} className="mb-12">
                            <h2 className="text-2xl font-semibold text-primary-color mb-6">{category.category}</h2>
                            <div className="flex flex-col gap-4">
                                {category.questions.map((faq, index) => {
                                    const id = `${category.category}-${index}`
                                    const isOpen = openItems[id]

                                    return (
                                        <div key={id} className="glass rounded-xl overflow-hidden">
                                            <button
                                                onClick={() => toggleItem(id)}
                                                className="w-full flex items-center justify-between p-6 text-left"
                                            >
                                                <span className="text-base font-semibold text-primary-color pr-4">{faq.question}</span>
                                                {isOpen ? (
                                                    <ChevronUp className="w-5 h-5 text-primary-base flex-shrink-0" />
                                                ) : (
                                                    <ChevronDown className="w-5 h-5 text-primary-base flex-shrink-0" />
                                                )}
                                            </button>
                                            {isOpen && (
                                                <div className="px-6 pb-6">
                                                    <p className="text-secondary-color leading-relaxed">{faq.answer}</p>
                                                </div>
                                            )}
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    ))}

                    {filteredCategories.length === 0 && (
                        <div className="text-center py-12">
                            <p className="text-secondary-color mb-4">No questions found matching "{searchQuery}"</p>
                            <Button onClick={() => setSearchQuery('')} variant="secondary">Clear search</Button>
                        </div>
                    )}
                </div>
            </Section>

            {/* Still have questions */}
            <Section glass>
                <div className="max-w-4xl mx-auto text-center">
                    <div className="glass-prominent p-12 lg:p-16 rounded-2xl">
                        <h2 className="text-3xl font-semibold text-primary-color mb-4">
                            Still have questions?
                        </h2>
                        <p className="text-lg text-secondary-color mb-8">
                            Our support team is here to help. We typically respond within 2 hours.
                        </p>
                        <div className="flex flex-wrap justify-center gap-4">
                            <Button variant="primary" size="lg">Contact Support</Button>
                            <Button variant="secondary" size="lg" to="/signup">Start Free Trial</Button>
                        </div>
                    </div>
                </div>
            </Section>
        </>
    )
}
